import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  initializeAuth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendEmailVerification,
  updateProfile,
  signInAnonymously,
  type Auth,
  type User as FirebaseUser,
} from "firebase/auth";
import { getAnalytics, setUserId, type Analytics } from "firebase/analytics";

import { env } from "@services/config/env";

const firebaseConfig = {
  apiKey: env.firebaseApiKey,
  authDomain: env.firebaseAuthDomain,
  projectId: env.firebaseProjectId,
  storageBucket: env.firebaseStorageBucket,
  messagingSenderId: env.firebaseMessagingSenderId,
  appId: env.firebaseAppId,
  measurementId: env.firebaseMeasurementId,
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let analytics: Analytics | null = null;

/**
 * Get or initialize Firebase Auth singleton.
 * Initializes Firebase app only once in production mode (skipped in mock/test).
 * Uses environment variables from Vite config for API keys and project settings.
 * @returns Firebase Auth instance for sign-in, sign-up, and token management
 * @throws Error if Firebase initialization fails or environment variables are missing
 */
function getFirebaseAuth(): Auth {
  if (!auth) {
    app = initializeApp(firebaseConfig);
    // Use localStorage-backed persistence instead of the default IndexedDB.
    // iOS Safari kills the IndexedDB connection when a tab is backgrounded,
    // causing "Connection to Indexed Database server lost" on return.
    auth = initializeAuth(app, {
      persistence: browserLocalPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
    // Initialize Analytics alongside Auth (only in browser, not SSR/test)
    if (typeof window !== "undefined" && env.firebaseMeasurementId) {
      analytics = getAnalytics(app);
    }
  }
  return auth;
}

/** Returns the Firebase Analytics instance, initializing it if needed. */
export function getFirebaseAnalytics(): Analytics | null {
  getFirebaseAuth(); // ensures app + analytics are initialized
  return analytics;
}

/**
 * Returns the current Firebase User object, or null if not signed in.
 * Use this to read displayName, email, photoURL directly from Firebase.
 */
export function getFirebaseCurrentUser(): FirebaseUser | null {
  return getFirebaseAuth().currentUser;
}

export async function firebaseSignIn(
  email: string,
  password: string,
): Promise<{ idToken: string; uid: string }> {
  const fbAuth = getFirebaseAuth();

  const credential = await signInWithEmailAndPassword(
    fbAuth,
    email,
    password,
  );

  const user = credential.user;

  // 🚨 Validación clave
  if (!user.emailVerified) {
    await signOut(fbAuth);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const error: any = new Error("Email not verified");
    error.code = "auth/email-not-verified";
    throw error;
  }

  const idToken = await user.getIdToken();

  if (analytics) setUserId(analytics, user.uid);

  return { idToken, uid: user.uid };
}

export async function firebaseSignUp(
  email: string,
  password: string,
  fullName?: string,
): Promise<{ uid: string }> {
  const fbAuth = getFirebaseAuth();

  const credential = await createUserWithEmailAndPassword(
    fbAuth,
    email,
    password,
  );

  const user = credential.user;

  // ✅ Set display name
  if (fullName) {
    await updateProfile(user, { displayName: fullName });
  }

  // ✅ Enviar verificación
  await sendEmailVerification(user);

  // ❌ Cerrar sesión hasta verificar
  await signOut(fbAuth);

  return { uid: user.uid };
}

export async function firebaseSignOut(): Promise<void> {
  const fbAuth = getFirebaseAuth();
  await signOut(fbAuth);
  if (analytics) setUserId(analytics, null);
}

export async function resendEmailVerification(): Promise<void> {
  const fbAuth = getFirebaseAuth();
  const user = fbAuth.currentUser;

  if (!user) throw new Error("No authenticated user");

  await sendEmailVerification(user);
}

/**
 * Get current Firebase user's ID token if signed in.
 * Returns null if no user is authenticated.
 *
 * Uses authStateReady() to wait until Firebase has finished restoring the
 * session from localStorage before checking currentUser. Without this,
 * onAuthStateChanged fires immediately with null (SDK still loading),
 * causing valid sessions to appear expired on page reload.
 */
export async function getFirebaseIdToken(): Promise<string | null> {
  const fbAuth = getFirebaseAuth();
  // Wait for Firebase to finish reading persisted session from localStorage.
  // currentUser is null during this async restoration — reading it too early
  // always returns null even when the user is authenticated.
  await fbAuth.authStateReady();
  const user = fbAuth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

/** Subscribe to Firebase auth state changes. */
export function onFirebaseAuthChanged(
  callback: (user: FirebaseUser | null) => void,
): () => void {
  const fbAuth = getFirebaseAuth();
  return onAuthStateChanged(fbAuth, callback);
}

/**
 * Returns true if running inside an in-app browser (Instagram, Facebook, TikTok)
 * that restricts window.open() — making signInWithPopup unusable.
 * In these environments, signInWithRedirect is used instead.
 */
function isRestrictedWebView(): boolean {
  const ua = navigator.userAgent;
  return /Instagram|FBAN|FBAV|FB_IAB|TikTok\/|Musical_ly/i.test(ua);
}

/**
 * Retrieves the result of a pending signInWithRedirect, if any.
 * Returns null if no redirect result is pending (safe to call on every mount).
 */
export async function firebaseGetRedirectResult(): Promise<{
  idToken: string;
  uid: string;
  email: string;
  displayName: string | null;
} | null> {
  const fbAuth = getFirebaseAuth();
  const result = await getRedirectResult(fbAuth);
  if (!result) return null;
  const idToken = await result.user.getIdToken();
  if (analytics) setUserId(analytics, result.user.uid);
  return {
    idToken,
    uid: result.user.uid,
    email: result.user.email ?? "",
    displayName: result.user.displayName,
  };
}

export async function firebaseSignInWithGoogle(): Promise<{
  idToken: string;
  uid: string;
  email: string;
  displayName: string | null;
}> {
  const fbAuth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();

  if (isRestrictedWebView()) {
    // Popup is blocked in Instagram/Facebook/TikTok WebViews.
    // Use redirect flow instead — navigates the current tab to Google auth.
    // The credential is retrieved on return via firebaseGetRedirectResult().
    await signInWithRedirect(fbAuth, provider);
    // Unreachable: the page navigates away. TypeScript requires a throw/return.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err: any = new Error("Redirigiendo a Google");
    err.code = "auth/redirect-initiated";
    throw err;
  }

  const credential = await signInWithPopup(fbAuth, provider);
  const idToken = await credential.user.getIdToken();
  if (analytics) setUserId(analytics, credential.user.uid);
  return {
    idToken,
    uid: credential.user.uid,
    email: credential.user.email ?? "",
    displayName: credential.user.displayName,
  };
}

/**
 * Crea una sesión Firebase anónima (sin registro) para que el usuario pueda
 * usar el mapa de asientos numerados sin necesidad de cuenta.
 * El token resultante es aceptado por ApiSeatResource igual que uno registrado.
 */
export async function firebaseSignInAnonymously(): Promise<{ idToken: string; uid: string }> {
  const fbAuth = getFirebaseAuth();
  const credential = await signInAnonymously(fbAuth);
  const idToken = await credential.user.getIdToken();
  return { idToken, uid: credential.user.uid };
}
